import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PixelBoardRef } from '@/components/organisms/PixelBoard';
import { SketchBoard, Pixel } from '@/types';
import { Timestamp } from 'firebase/firestore';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { logPageView, logButtonClick } from '@/services/analytics.service';
import { PixelBoard } from '@/components/organisms/PixelBoard';
import {
  ColorPicker,
  COLOR_PALETTES,
} from '@/components/molecules/ColorPicker';
import { CanvasControls } from '@/components/molecules/CanvasControls';
import { BackButton } from '@/components/molecules/BackButton';
import { Loading } from '@/components/organisms/Loading';
import { Button } from '@/components/atoms/ui/button';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/atoms/ui/tooltip';
import { FiCheck, FiX } from 'react-icons/fi';
import { config } from '@/lib/config';

export const SketchBoardPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();
  const pageContainerRef = useRef<HTMLDivElement>(null);
  const pixelBoardRef = useRef<PixelBoardRef>(null);

  const [sketchBoard, setSketchBoard] = useState<SketchBoard | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedColor, setSelectedColor] = useState('#FFFFFF');
  const [pendingPixelsCount, setPendingPixelsCount] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [historyLength, setHistoryLength] = useState(1);
  const [visibleMenus, setVisibleMenus] = useState<Set<string>>(new Set());
  const [pixels, setPixels] = useState<(Pixel | null)[][]>([]);

  // Convert SketchBoard to Board format for PixelBoard component
  const boardForPixelBoard = sketchBoard
    ? {
        id: sketchBoard.localId,
        name: sketchBoard.name,
        ownerId: sketchBoard.ownerId || '',
        ownerUsername: sketchBoard.ownerUsername || 'Guest',
        width: sketchBoard.width,
        height: sketchBoard.height,
        maxPixels: sketchBoard.maxPixels,
        createdAt: {
          toDate: () => sketchBoard.createdAt,
        } as Timestamp,
        isPublic: true,
        customPalette: sketchBoard.customPalette,
      }
    : null;

  // Area definitions with thresholds (same as BoardPage)
  const OPEN_THRESHOLD = 0.3;
  const CLOSE_THRESHOLD = 0.1;
  const CORNER_SIZE = 0.15;
  const MENU_TIMEOUT = 2000;

  type Area =
    | 'top-left'
    | 'top'
    | 'top-right'
    | 'left'
    | 'right'
    | 'bottom-left'
    | 'bottom'
    | 'bottom-right';

  const menuAreas: Record<Area, string[]> = useMemo(
    () => ({
      'top-left': ['title'],
      top: ['quota'],
      'top-right': [],
      left: [],
      right: [],
      'bottom-left': ['info-display'],
      bottom: ['color-picker'],
      'bottom-right': ['canvas-controls'],
    }),
    []
  );

  const calculateAreaCertainty = useCallback(
    (
      x: number,
      y: number,
      width: number,
      height: number
    ): Record<Area, number> => {
      const normalizedX = x / width;
      const normalizedY = y / height;

      const certainties: Record<Area, number> = {
        'top-left': 0,
        top: 0,
        'top-right': 0,
        left: 0,
        right: 0,
        'bottom-left': 0,
        bottom: 0,
        'bottom-right': 0,
      };

      if (normalizedX < CORNER_SIZE && normalizedY < CORNER_SIZE) {
        const distX = normalizedX / CORNER_SIZE;
        const distY = normalizedY / CORNER_SIZE;
        certainties['top-left'] = 1 - Math.sqrt(distX * distX + distY * distY);
      }

      if (normalizedX > 1 - CORNER_SIZE && normalizedY < CORNER_SIZE) {
        const distX = (1 - normalizedX) / CORNER_SIZE;
        const distY = normalizedY / CORNER_SIZE;
        certainties['top-right'] = 1 - Math.sqrt(distX * distX + distY * distY);
      }

      if (normalizedX < CORNER_SIZE && normalizedY > 1 - CORNER_SIZE) {
        const distX = normalizedX / CORNER_SIZE;
        const distY = (1 - normalizedY) / CORNER_SIZE;
        certainties['bottom-left'] =
          1 - Math.sqrt(distX * distX + distY * distY);
      }

      if (normalizedX > 1 - CORNER_SIZE && normalizedY > 1 - CORNER_SIZE) {
        const distX = (1 - normalizedX) / CORNER_SIZE;
        const distY = (1 - normalizedY) / CORNER_SIZE;
        certainties['bottom-right'] =
          1 - Math.sqrt(distX * distX + distY * distY);
      }

      if (
        normalizedY < OPEN_THRESHOLD &&
        normalizedX >= CORNER_SIZE &&
        normalizedX <= 1 - CORNER_SIZE
      ) {
        certainties['top'] = 1 - normalizedY / OPEN_THRESHOLD;
      }

      if (
        normalizedY > 1 - OPEN_THRESHOLD &&
        normalizedX >= CORNER_SIZE &&
        normalizedX <= 1 - CORNER_SIZE
      ) {
        certainties['bottom'] = 1 - (1 - normalizedY) / OPEN_THRESHOLD;
      }

      if (
        normalizedX < OPEN_THRESHOLD &&
        normalizedY >= CORNER_SIZE &&
        normalizedY <= 1 - CORNER_SIZE
      ) {
        certainties['left'] = 1 - normalizedX / OPEN_THRESHOLD;
      }

      if (
        normalizedX > 1 - OPEN_THRESHOLD &&
        normalizedY >= CORNER_SIZE &&
        normalizedY <= 1 - CORNER_SIZE
      ) {
        certainties['right'] = 1 - (1 - normalizedX) / OPEN_THRESHOLD;
      }

      return certainties;
    },
    []
  );

  const menuOpenTimesRef = useRef<Map<string, number>>(new Map());
  const menuTimeoutsRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const visibleMenusRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (!isFullscreen) {
      const allMenus = new Set([
        'title',
        'quota',
        'color-picker',
        'canvas-controls',
        'info-display',
      ]);
      visibleMenusRef.current = allMenus;
      setVisibleMenus(allMenus);
      return;
    }

    const initialMenus = new Set([
      'title',
      'quota',
      'color-picker',
      'canvas-controls',
      'info-display',
    ]);
    visibleMenusRef.current = initialMenus;
    setVisibleMenus(initialMenus);

    const now = Date.now();
    initialMenus.forEach((menu) => {
      menuOpenTimesRef.current.set(menu, now);
    });

    const handleMouseMove = (e: MouseEvent) => {
      const width = window.innerWidth;
      const height = window.innerHeight;
      const certainties = calculateAreaCertainty(
        e.clientX,
        e.clientY,
        width,
        height
      );
      const now = Date.now();
      const currentVisibleMenus = new Set(visibleMenusRef.current);
      const newVisibleMenus = new Set<string>();

      Object.entries(certainties).forEach(([area, certainty]) => {
        const menus = menuAreas[area as Area];

        menus.forEach((menu) => {
          const isCurrentlyVisible = currentVisibleMenus.has(menu);
          const menuOpenTime = menuOpenTimesRef.current.get(menu) || 0;
          const timeSinceOpen = now - menuOpenTime;
          const isWithinTimeout = timeSinceOpen < MENU_TIMEOUT;

          const existingTimeout = menuTimeoutsRef.current.get(menu);
          if (existingTimeout) {
            clearTimeout(existingTimeout);
            menuTimeoutsRef.current.delete(menu);
          }

          let shouldBeVisible = false;

          if (certainty >= OPEN_THRESHOLD) {
            shouldBeVisible = true;
            if (!isCurrentlyVisible) {
              menuOpenTimesRef.current.set(menu, now);
            }
          } else if (isCurrentlyVisible) {
            if (isWithinTimeout) {
              shouldBeVisible = true;
            } else if (certainty >= CLOSE_THRESHOLD) {
              shouldBeVisible = true;
            } else {
              shouldBeVisible = false;
              menuOpenTimesRef.current.delete(menu);
            }
          }

          if (shouldBeVisible) {
            newVisibleMenus.add(menu);
          }
        });
      });

      currentVisibleMenus.forEach((menu) => {
        if (!newVisibleMenus.has(menu)) {
          menuOpenTimesRef.current.delete(menu);
          const timeout = menuTimeoutsRef.current.get(menu);
          if (timeout) {
            clearTimeout(timeout);
            menuTimeoutsRef.current.delete(menu);
          }
        }
      });

      visibleMenusRef.current = newVisibleMenus;
      setVisibleMenus(newVisibleMenus);
    };

    window.addEventListener('mousemove', handleMouseMove);
    const timeouts = menuTimeoutsRef.current;
    const openTimes = menuOpenTimesRef.current;
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      openTimes.clear();
    };
  }, [isFullscreen, calculateAreaCertainty, menuAreas]);

  useEffect(() => {
    logPageView('Sketch Board Page');
  }, []);

  useEffect(() => {
    // Always create a fresh sketch board (purely in-memory, no persistence)
    const localId = crypto.randomUUID();
    const randomPalette = COLOR_PALETTES[Math.floor(Math.random() * COLOR_PALETTES.length)];
    
    const newSketchBoard: SketchBoard = {
      localId,
      name: 'Untitled Sketch',
      width: config.defaultBoardWidth,
      height: config.defaultBoardHeight,
      maxPixels: config.defaultBoardWidth * config.defaultBoardHeight,
      customPalette: randomPalette.colors.map((c) => c),
      createdAt: new Date(),
      ownerId: user?.uid,
      ownerUsername: user?.username,
    };

    // Initialize empty pixels array
    const emptyPixels: (null)[][] = Array(config.defaultBoardHeight)
      .fill(null)
      .map(() => Array(config.defaultBoardWidth).fill(null));

    // Set initial color
    if (newSketchBoard.customPalette && newSketchBoard.customPalette.length > 0) {
      setSelectedColor(newSketchBoard.customPalette[0]);
    } else {
      if (COLOR_PALETTES[0].colors.length > 0) {
        setSelectedColor(COLOR_PALETTES[0].colors[0]);
      }
    }

    setSketchBoard(newSketchBoard);
    setPixels(emptyPixels);
    setLoading(false);
  }, [user]);

  const handleBatchPixelPlace = async (
    pixelsToPlace: { x: number; y: number; color: string }[]
  ) => {
    if (!sketchBoard) return;

    try {
      // Update pixels directly (no quota check for sketch boards)
      const newPixels = pixels.map((row) => [...row]);
      const now = new Date();

      pixelsToPlace.forEach(({ x, y, color }) => {
        if (y >= 0 && y < newPixels.length && x >= 0 && x < newPixels[y].length) {
          newPixels[y][x] = {
            color,
            placedBy: user?.uid || 'guest',
            placedByUsername: user?.username || 'Guest',
            placedAt: {
              toDate: () => now,
            } as Timestamp,
          };
        }
      });

      setPixels(newPixels);

      toast({
        title: 'Pixels applied!',
        description: `Successfully placed ${pixelsToPlace.length} pixel${pixelsToPlace.length !== 1 ? 's' : ''}`,
      });
    } catch (error) {
      console.error('Error placing pixels:', error);
      toast({
        title: 'Failed to place pixels',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
      throw error;
    }
  };

  const handleApplyChanges = () => {
    logButtonClick('Apply Changes', 'SketchBoardPage');
    pixelBoardRef.current?.applyChanges();
  };

  const handleClearPending = () => {
    logButtonClick('Clear Pending', 'SketchBoardPage');
    pixelBoardRef.current?.clearPending();
  };

  const handleZoomIn = () => {
    logButtonClick('Zoom In', 'SketchBoardPage');
    pixelBoardRef.current?.zoomIn();
  };

  const handleZoomOut = () => {
    logButtonClick('Zoom Out', 'SketchBoardPage');
    pixelBoardRef.current?.zoomOut();
  };

  const handleResetView = () => {
    logButtonClick('Reset View', 'SketchBoardPage');
    pixelBoardRef.current?.resetView();
  };

  const handleFullscreenToggle = useCallback(() => {
    const container = pageContainerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container
        .requestFullscreen()
        .then(() => {
          setIsFullscreen(true);
        })
        .catch((err) => {
          console.error('Error entering fullscreen:', err);
          toast({
            title: 'Fullscreen Error',
            description: 'Could not enter fullscreen mode',
            variant: 'destructive',
          });
        });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      });
    }
  }, [toast]);

  useEffect(() => {
    const handleFullscreenChange = () => {
      const isFullscreenNow = !!document.fullscreenElement;
      setIsFullscreen(isFullscreenNow);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () =>
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  if (loading) {
    return (
      <Loading
        message="Loading sketch board..."
        bgColor="bg-gray-900"
        textColor="text-gray-300"
      />
    );
  }

  if (!sketchBoard || !boardForPixelBoard) {
    return null;
  }

  return (
    <div ref={pageContainerRef} className="h-screen relative overflow-hidden">
      <PixelBoard
        ref={pixelBoardRef}
        board={boardForPixelBoard}
        pixels={pixels}
        selectedColor={selectedColor}
        onBatchPixelPlace={handleBatchPixelPlace}
        isGuest={false} // Allow guest users to draw on sketch boards
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        onResetView={handleResetView}
        onPendingPixelsChange={setPendingPixelsCount}
        onPendingPixelsNeedingQuotaChange={() => {}} // No quota for sketch boards
        onQuotaExceeded={() => {}} // No quota for sketch boards
        currentUserId={user?.uid || 'guest'} // Use 'guest' as fallback for guest users
        availableQuota={999999} // Unlimited quota for sketch boards
        onFullscreenChange={setIsFullscreen}
        onHistoryStateChange={(index: number, length: number) => {
          setHistoryIndex(index);
          setHistoryLength(length);
        }}
        onUndoRequest={() => {
          pixelBoardRef.current?.undo();
        }}
        onRedoRequest={() => {
          pixelBoardRef.current?.redo();
        }}
        showInfoDisplay={!isFullscreen || visibleMenus.has('info-display')}
        showGuestWarning={false}
      />

      {/* Top-Left Menu */}
      <div className="absolute top-4 left-4 z-30 pointer-events-auto">
        <div
          className={`flex items-center gap-2 bg-black/20 backdrop-blur-xl border border-white/10 rounded-2xl px-2 py-2 transition-all duration-300 ease-out ${
            isFullscreen
              ? visibleMenus.has('title')
                ? 'translate-x-0 translate-y-0 opacity-100'
                : '-translate-x-full -translate-y-full opacity-0'
              : 'translate-x-0 translate-y-0 opacity-100'
          }`}
        >
          <BackButton
            pageName="SketchBoardPage"
            variant="board"
            title="Back to Home"
            onClick={() => navigate('/')}
          />
          <div className="border-l pl-3 pr-2">
            <h1 className="text-lg font-bold leading-tight text-white">
              {sketchBoard.name}
            </h1>
            <p className="text-xs" style={{ color: '#929bc9', opacity: 0.7 }}>
              <span className="px-1.5 py-0.5 rounded bg-primary/20 text-primary text-xs mr-1">
                Sketch
              </span>
              {sketchBoard.width} × {sketchBoard.height}
            </p>
          </div>
        </div>
      </div>

      {/* Top-Center Menu */}
      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
        <div
          className={`flex items-center gap-3 transition-all duration-300 ease-out ${
            isFullscreen
              ? visibleMenus.has('quota')
                ? 'translate-x-0 translate-y-0 opacity-100'
                : 'translate-x-0 -translate-y-full opacity-0'
              : 'translate-x-0 translate-y-0 opacity-100'
          }`}
        >
          {pendingPixelsCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleClearPending}
                  style={{
                    color: '#929bc9',
                    borderColor: 'rgba(146, 155, 201, 0.2)',
                  }}
                  className="rounded-full w-10 h-10 p-0 bg-black/20 backdrop-blur-xl border border-white/10 hover:bg-white/20"
                >
                  <FiX className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Clear</p>
              </TooltipContent>
            </Tooltip>
          )}

          <div className="bg-black/20 backdrop-blur-xl border border-white/10 rounded-2xl px-4 py-3">
            <p className="text-sm text-white">
              {pendingPixelsCount > 0 ? (
                <>
                  <span className="font-bold">{pendingPixelsCount}</span> pending
                </>
              ) : (
                <span className="opacity-70">Sketch Board</span>
              )}
            </p>
          </div>

          {pendingPixelsCount > 0 && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  size="sm"
                  onClick={handleApplyChanges}
                  className="rounded-full w-10 h-10 p-0 bg-black/20 backdrop-blur-xl border border-white/10 hover:bg-white/20"
                >
                  <FiCheck className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent>
                <p>Apply</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </div>


      {/* Bottom-Center Menu */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 pointer-events-auto">
        <div
          className={`w-auto max-w-2xl bg-black/20 backdrop-blur-xl border border-white/10 rounded-2xl transition-all duration-300 ease-out ${
            isFullscreen
              ? visibleMenus.has('color-picker')
                ? 'translate-x-0 translate-y-0 opacity-100'
                : 'translate-x-0 translate-y-full opacity-0'
              : 'translate-x-0 translate-y-0 opacity-100'
          }`}
        >
          <ColorPicker
            selectedColor={selectedColor}
            onColorChange={setSelectedColor}
            colors={
              sketchBoard.customPalette && sketchBoard.customPalette.length > 0
                ? sketchBoard.customPalette
                : COLOR_PALETTES[0].colors
            }
          />
        </div>
      </div>

      {/* Bottom-Right Menu */}
      <div className="absolute bottom-6 right-4 z-30 pointer-events-auto">
        <div
          className={`transition-all duration-300 ease-out ${
            isFullscreen
              ? visibleMenus.has('canvas-controls')
                ? 'translate-x-0 translate-y-0 opacity-100'
                : 'translate-x-full translate-y-full opacity-0'
              : 'translate-x-0 translate-y-0 opacity-100'
          }`}
        >
          <CanvasControls
            isGuest={false} // Allow guest users to use controls on sketch boards
            isFullscreen={isFullscreen}
            historyIndex={historyIndex}
            historyLength={historyLength}
            onUndo={() => {
              pixelBoardRef.current?.undo();
            }}
            onRedo={() => {
              pixelBoardRef.current?.redo();
            }}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onFullscreenToggle={handleFullscreenToggle}
          />
        </div>
      </div>
    </div>
  );
};


